import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Clock, Ticket, ArrowRight } from "lucide-react";
import { endpoints } from "@/lib/api";
import StayTuned from "@/components/StayTuned";
import { PageHero } from "./MarketingLayout";

export default function MainEvent() {
  const [event, setEvent] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [judges, setJudges] = useState([]);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    endpoints.event().then(setEvent).catch(() => {});
    endpoints.schedule().then(setSchedule).catch(() => {});
    endpoints.judges().then(setJudges).catch(() => {});
    endpoints.ticketTiers().then(setTiers).catch(() => {});
  }, []);

  const days = [...new Set(schedule.map((s) => s.day))];

  return (
    <div>
      <PageHero eyebrow="The Main Event" title={<>The 2027<br />Salon</>} image={event?.hero_poster}
        subtitle="Two huge days of Perth's finest custom builds, live entertainment, judging and People's Choice voting at the Perth Convention Centre." />

      <section className="max-w-7xl mx-auto px-6 -mt-4 pb-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Calendar, label: "When", value: "Dates to be announced" },
            { icon: MapPin, label: "Where", value: "Perth Convention Centre" },
            { icon: Ticket, label: "Tickets", value: "From $35 AUD" },
          ].map((it) => (
            <div key={it.label} className="border border-white/10 bg-[#121212] p-6 flex items-center gap-4">
              <it.icon className="text-[#ff7a1a]" size={28} />
              <div>
                <div className="font-heading uppercase text-xs text-[#8a8d91]">{it.label}</div>
                <div className="font-heading text-lg text-white">{it.value}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10"><StayTuned /></div>
      </section>

      {/* Schedule */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="schedule-section">
        <h2 className="font-display text-4xl sm:text-6xl uppercase text-white mb-8">Run Sheet</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {days.map((day) => (
            <div key={day}>
              <div className="font-heading uppercase text-[#b026ff] tracking-widest text-sm mb-4">{day}</div>
              <div className="space-y-px">
                {schedule.filter((s) => s.day === day).map((s) => (
                  <div key={s.id} className="flex gap-4 items-center bg-[#121212] border border-white/10 px-4 py-3">
                    <div className="font-display text-2xl text-[#ffd600] tabular-nums min-w-[70px]">
                      {new Date(s.start).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}
                    </div>
                    <div>
                      <div className="font-heading text-white uppercase leading-tight">{s.title}</div>
                      <div className="text-xs text-[#8a8d91] flex items-center gap-1"><MapPin size={11} /> {s.location}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tickets */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="tickets-preview">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-6xl uppercase text-white">Tickets</h2>
          <Link to="/app/tickets" className="pas-btn text-sm">Buy in app <ArrowRight size={16} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t) => (
            <div key={t.id} className="border border-white/10 bg-[#121212] p-6 card-hover flex flex-col">
              <div className="font-heading uppercase text-white text-lg">{t.name}</div>
              <div className="font-display text-5xl pas-gradient-text my-3">${t.price}</div>
              <ul className="space-y-1.5 text-sm text-[#8a8d91] font-body flex-1">
                {t.perks.map((p, i) => <li key={i}>· {p}</li>)}
              </ul>
              <Link to="/app/tickets" className="pas-btn mt-5 text-sm w-full">Get tickets</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Judges */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <h2 className="font-display text-4xl sm:text-6xl uppercase text-white mb-8">Meet the Judges</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {judges.map((j) => (
            <div key={j.id} className="border border-white/10 bg-[#121212] p-6 card-hover">
              <div className="w-16 h-16 pas-gradient grid place-items-center font-display text-3xl text-black mb-4">
                {j.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <div className="font-heading text-white text-lg uppercase">{j.name}</div>
              <div className="text-[#ff7a1a] text-xs font-heading uppercase mb-2">{j.role}</div>
              <p className="text-[#8a8d91] text-sm font-body">{j.bio}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
